import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import {
  HeartPulse,
  ScanLine,
  Microscope,
  Dna,
  Brain,
  Activity,
  Watch,
  Gauge,
  Flame,
  Box,
} from "lucide-react";

const KIND_ICON = {
  laboratory: HeartPulse,
  radiology: ScanLine,
  pathology: Microscope,
  genomics: Dna,
  neuro_signal: Brain,
  biomechanics: Activity,
  wearable: Watch,
  health_aggregator: Watch,
  bioimpedance: Gauge,
  energy_medicine: Flame,
  therapy_chamber: Flame,
  iot: Box,
  custom: Box,
};

const KIND_LABEL = {
  laboratory: { tr: "Laboratuvar", en: "Laboratory" },
  radiology: { tr: "Radyoloji", en: "Radiology" },
  pathology: { tr: "Patoloji", en: "Pathology" },
  genomics: { tr: "Genomik", en: "Genomics" },
  neuro_signal: { tr: "Nöro-sinyal (EEG / qEEG / fNIRS)", en: "Neuro signals (EEG / qEEG / fNIRS)" },
  biomechanics: { tr: "Biyomekanik (EMG / Hareket)", en: "Biomechanics (EMG / Motion)" },
  health_aggregator: { tr: "Sağlık Toplayıcısı", en: "Health Aggregator" },
  wearable: { tr: "Giyilebilir", en: "Wearable" },
  bioimpedance: { tr: "Biyoempedans", en: "Bioimpedance" },
  energy_medicine: { tr: "Enerji Tıbbı", en: "Energy Medicine" },
  therapy_chamber: { tr: "Terapi Kabini", en: "Therapy Chamber" },
  iot: { tr: "IoT", en: "IoT" },
  custom: { tr: "Özel", en: "Custom" },
};

export default function PluginsPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [active, setActive] = useState(null);
  const [ingestResult, setIngestResult] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const isMember = user?.role === "member";

  useEffect(() => {
    api.get("/plugins").then((r) => setData(r.data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    if (isMember) {
      api.get("/umie/summary").then((r) => setSummary(r.data)).catch(() => {});
    }
  }, [isMember]);

  async function trySample(plugin) {
    setBusyId(plugin.id);
    setIngestResult(null);
    try {
      const { data: res } = await api.post(`/plugins/${plugin.id}/ingest`, { raw: plugin.sample_payload });
      setIngestResult({ pluginId: plugin.id, ...res });
      if (isMember) {
        const s = await api.get("/umie/summary");
        setSummary(s.data);
      }
    } catch (e) {
      setIngestResult({ pluginId: plugin.id, error: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusyId(null);
    }
  }

  const groups = useMemo(() => data?.by_kind || {}, [data]);

  return (
    <div className="space-y-8" data-testid="plugins-page">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          Plugin SDK · UMIE
        </div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
          {language === "tr" ? "Plugin Kataloğu" : "Plugin Catalog"}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-slate-500">
          {language === "tr"
            ? "Her cihaz, laboratuvar veya yazılım entegrasyonu bağımsız bir plugin'dir. ME Core'da vendor mantığı yoktur — yeni bir cihaz tek dosyayla eklenir."
            : "Every device, lab or software integration is an independent plugin. ME Core contains no vendor logic — adding a new device is a single file change."}
        </p>
      </header>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {isMember && summary && <UMIESummaryCard summary={summary} language={language} />}

      {!data ? (
        <div className="text-sm text-slate-400">{t.common.loading}</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groups).map(([kind, items]) => {
            const Icon = KIND_ICON[kind] || Box;
            const label = (KIND_LABEL[kind] && KIND_LABEL[kind][language]) || kind;
            return (
              <section key={kind} className="mios-card" data-testid={`plugin-group-${kind}`}>
                <div className="mios-card-header">
                  <div className="flex items-center gap-2.5">
                    <Icon className="h-4 w-4 text-slate-500" />
                    <div className="mios-card-title">{label}</div>
                  </div>
                  <div className="mios-card-sub">{items.length}</div>
                </div>
                <div className="mios-card-body grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {items.map((p) => (
                    <div key={p.id} className={`rounded-lg border border-slate-200 bg-white p-4 transition-colors ${active === p.id ? "ring-2 ring-[color:var(--mios-brand)]" : ""}`} data-testid={`plugin-card-${p.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-sm font-medium text-slate-900">{p.vendor}</div>
                          <div className="mono text-[11px] text-slate-500">{p.id} · v{p.version}</div>
                        </div>
                        <span className={`mios-pill ${p.requires_native_bridge ? "mios-pill-warning" : "mios-pill-normal"}`}>
                          {p.requires_native_bridge ? (language === "tr" ? "Köprü gerekli" : "Needs bridge") : (language === "tr" ? "Hazır" : "Ready")}
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-slate-600">{p.description}</div>
                      <div className="mt-3 flex flex-wrap gap-1">
                        {p.capabilities.slice(0, 4).map((c) => (
                          <span key={c} className="mios-pill mios-pill-low mono">{c}</span>
                        ))}
                      </div>
                      <div className="mt-4 flex gap-2">
                        <button
                          type="button"
                          onClick={() => setActive(active === p.id ? null : p.id)}
                          className="mios-btn mios-btn-ghost flex-1"
                          data-testid={`plugin-inspect-${p.id}`}
                        >
                          {language === "tr" ? "Şema" : "Schema"}
                        </button>
                        {isMember && (
                          <button
                            type="button"
                            onClick={() => trySample(p)}
                            disabled={busyId === p.id}
                            className="mios-btn mios-btn-primary flex-1"
                            data-testid={`plugin-try-${p.id}`}
                          >
                            {busyId === p.id ? "…" : (language === "tr" ? "Demoyu yükle" : "Ingest sample")}
                          </button>
                        )}
                      </div>
                      {active === p.id && (
                        <pre className="mono mt-3 max-h-48 overflow-auto rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-700" data-testid={`plugin-schema-${p.id}`}>
{JSON.stringify(p.sample_payload, null, 2)}
                        </pre>
                      )}
                      {ingestResult?.pluginId === p.id && (
                        <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid={`plugin-result-${p.id}`}>
                          {ingestResult.error
                            ? ingestResult.error
                            : (language === "tr"
                                ? `${ingestResult.count} gözlem UMIE'ye akıtıldı.`
                                : `${ingestResult.count} observations streamed into UMIE.`)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UMIESummaryCard({ summary, language }) {
  return (
    <section className="mios-card" data-testid="umie-summary">
      <div className="mios-card-header">
        <div className="mios-card-title">
          {language === "tr" ? "UMIE · Normalize edilmiş gözlemler" : "UMIE · Normalised observations"}
        </div>
        <div className="mios-card-sub">{summary.total}</div>
      </div>
      <div className="mios-card-body">
        {summary.categories.length === 0 ? (
          <div className="text-sm text-slate-400">
            {language === "tr"
              ? "Henüz gözlem yok — aşağıdan örnek bir plugin yükleyerek başlayın."
              : "No observations yet — try ingesting a sample plugin below."}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {summary.categories.map((c) => (
              <div key={c.category} className="rounded-lg border border-slate-200 bg-white px-3 py-3" data-testid={`umie-category-${c.category}`}>
                <div className="mios-card-sub">{(KIND_LABEL[c.category] && KIND_LABEL[c.category][language]) || c.category}</div>
                <div className="mt-1 flex items-baseline gap-1.5">
                  <div className="mono text-2xl font-semibold text-slate-900">{c.count}</div>
                  <div className="text-xs text-slate-500">obs</div>
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  {c.plugins.join(" · ")}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
